import { contentTopicRequest } from '../../questionBank/qbankNavigation';
import { EXPLORE_ROUTES } from "../exploreRoutes";
import { noteNeedsPurchase, promptNoteSubscription, noteDetailFlow } from './notesAccess';
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import QBankFilter from "../../questionBank/QBankFilter";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../../questionBank/QuestionBankList.css";
import { LanguageMobileIcon, BackSVG } from "../../components/SvgIcons";
import { useSidebarData } from "../../hooks/useSidebarData";
import { useNotesList } from "../../hooks/useNotesList";
import { useNotesListActions } from "../../hooks/useNotesFeedback";
import NotesReportModal from "./NotesReportModal";
import NotesStickyNotesPopup from "./NotesStickyNotesPopup";
import NotesViewModal from "./NotesViewModal";
import {
  resolveQuestionBankStateId,
  resolveQuestionBankStateName,
} from "../../questionBank/stateUtils";
import {
  buildQuestionBankFlowState,
  buildQuestionBankSubjectsState,
  persistQuestionBankFlowState,
} from "../../questionBank/qbankFlowState";
import bookmark from "../../assets/images/bookmark.png";
import { CONTENT_ROUTES } from "../contentRoutes";

const NOTES_CATEGORY_ID = 4;
const DEFAULT_SUBJECT_ID = 1;

function buildVisiblePages(currentPage, totalPages) {
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const halfWindow = Math.floor(maxVisiblePages / 2);
  let startPage = Math.max(1, currentPage - halfWindow);
  let endPage = startPage + maxVisiblePages - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = endPage - maxVisiblePages + 1;
  }

  return Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
}

function mapFiltersToApiParams(filters = {}) {
  const mappings = {
    bookmark_status: "bookmark_status",
    report_status: "report_status",
    languages: "language",
    tags: "notes_tags",
    personal_notes: "personal_notes",
    read_unread: "read_unread",
    free_paid_filter: "free_paid",
  };

  return Object.entries(filters).reduce((accumulator, [key, value]) => {
    if (value === undefined || value === null || value === "") {
      return accumulator;
    }

    accumulator[mappings[key] || key] = value;
    return accumulator;
  }, {});
}

const SummaryRowShimmer = () => (
  <div className="qbl-summaryRow" aria-hidden="true">
    <span className="dashboard-skeleton dashboard-skeleton--title qbl-summaryRow__shimmer-title" />
    <span className="dashboard-skeleton dashboard-skeleton--button qbl-summaryRow__shimmer-guess" />
  </div>
);

const NoteCardShimmer = () => (
  <article className="qbl-questionCard" aria-hidden="true">
    <div className="qbl-questionCard__head">
      <div className="qbl-questionCard__meta">
        <span className="dashboard-skeleton dashboard-skeleton--button qbl-questionCard__shimmer-badge" />
        <span className="dashboard-skeleton dashboard-skeleton--icon qbl-questionCard__shimmer-lock" />
      </div>
      <span className="dashboard-skeleton dashboard-skeleton--icon qbl-questionCard__shimmer-bookmark" />
    </div>
    <span className="dashboard-skeleton dashboard-skeleton--line qbl-questionCard__shimmer-line" />
    <span className="dashboard-skeleton dashboard-skeleton--line qbl-questionCard__shimmer-line" />
    <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--text-short qbl-questionCard__shimmer-line" />
    <div className="qbl-questionCard__footer">
      {Array.from({ length: 4 }).map((_, index) => (
        <span key={index} className="dashboard-skeleton dashboard-skeleton--line qbl-questionCard__shimmer-footer" />
      ))}
    </div>
  </article>
);

function getNotesTypeIconClass(item) {
  const noteType = String(item?.notesType || "").toUpperCase();

  if (noteType === "F") {
    return "bi-file-earmark-pdf-fill";
  }

  return item?.isRead ? "bi-file-richtext-fill" : "bi-file-text-fill";
}

const NoteCard = ({
  item,
  onBookmark,
  onLike,
  onView,
  onReport,
  onNotes,
  onOpen,
  isBookmarkUpdating = false,
  isLikeUpdating = false,
}) => (
  <article
    className={`qbl-questionCard${item.isBold ? " qbl-questionCard--active" : ""}`}
    role="button"
    tabIndex={0}
    onClick={() => onOpen(item)}
    onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onOpen(item);
      }
    }}
  >
    <div className="qbl-questionCard__head">
      <div className="qbl-questionCard__meta">
        <span className="qbl-questionCard__number">{String(item.serialNo).padStart(2, "0")}</span>
        <span className="qbl-questionCard__lock" aria-hidden="true">
          <i className={`bi ${item.paymentStatus === 1 ? "bi-unlock-fill" : "bi-lock-fill"}`} />
        </span>
      </div>

      <div className="qbl-questionCard__actions">
        <span className="qbl-questionCard__bookmark qbl-questionCard__bookmark--type" aria-label={item.notesType === "F" ? "File note" : "Text note"}>
          <i className={`bi ${getNotesTypeIconClass(item)}`} aria-hidden="true" />
        </span>
        <button type="button" className="qbl-questionCard__bookmark" aria-label="Bookmark note" onClick={(event) => { event.stopPropagation(); onBookmark(item); }} disabled={isBookmarkUpdating}>
          {item.isBookmarked ? <img src={bookmark} alt="" /> : <i className="bi bi-bookmark" aria-hidden="true" />}
        </button>
      </div>
    </div>

    <p
      className="qbl-questionCard__question"
    >
      {item.title}
    </p>
    {item.viewLabel ? <p className="qbl-questionCard__descrip">{item.viewLabel}</p> : null}
    <p className="qbl-questionCard__descrip">{item.description}</p>
    {item.subscriptionMessage ? <p className="qbl-questionCard__descrip text-danger">{item.subscriptionMessage}</p> : null}

    <div className="qbl-questionCard__footer">
      <button type="button" className="qbl-questionCard__footerItem" onClick={(event) => { event.stopPropagation(); onLike(item); }} disabled={isLikeUpdating}>
        <i className={`bi ${item.isLiked ? "bi-heart-fill" : "bi-heart"}`} aria-hidden="true" />
        <span>Like</span>
        <strong>{item.likeCount}</strong>
      </button>

      <button type="button" className="qbl-questionCard__footerItem" onClick={(event) => { event.stopPropagation(); onView(item); }}>
        <i className="bi bi-eye" aria-hidden="true" />
        <span>View</span>
        <strong>{item.viewCount}</strong>
      </button>

      <button type="button" className="qbl-questionCard__footerItem" onClick={(event) => { event.stopPropagation(); onReport(item); }}>
        <i className="bi bi-exclamation-circle" aria-hidden="true" />
        <span>Report</span>
        <strong>{item.reportCount}</strong>
      </button>

      <button type="button" className="qbl-questionCard__footerItem" onClick={(event) => { event.stopPropagation(); onNotes(item); }}>
        <i className="bi bi-journal-text" aria-hidden="true" />
        <span>Notes</span>
        <strong>{item.notesCount}</strong>
      </button>
    </div>
  </article>
);

const NotesList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: sidebarData = null } = useSidebarData();
  const flowState = useMemo(() => buildQuestionBankFlowState(location.state), [location.state]);
  const initialListQueryState = flowState?.listQueryState || {};
  const [searchTerm, setSearchTerm] = useState(String(initialListQueryState.searchTerm || ""));
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(String(initialListQueryState.searchTerm || ""));
  const [appliedFilters, setAppliedFilters] = useState(initialListQueryState.appliedFilters || {});
  const [currentPage, setCurrentPage] = useState(Number(initialListQueryState.currentPage || 1));
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const resolvedStateId = useMemo(
    () =>
      resolveQuestionBankStateId({
        locationState: flowState,
        sidebarData,
      }),
    [flowState, sidebarData]
  );


  const filterQueryParams = useMemo(
    () => ({
      states_id: resolvedStateId,
      category_id: NOTES_CATEGORY_ID,
    }),
    [resolvedStateId]
  );

  const notesQueryParams = useMemo(
    () => ({
      ...filterQueryParams,
      subject_id: Number(flowState?.selectedSubject?.id || flowState?.subject_id || DEFAULT_SUBJECT_ID),
      ...contentTopicRequest(flowState),
      page: currentPage,
      ...(debouncedSearchTerm.trim() ? { search_term: debouncedSearchTerm.trim() } : {}),
      ...mapFiltersToApiParams(appliedFilters),
    }),
    [appliedFilters, currentPage, debouncedSearchTerm, filterQueryParams, flowState]
  );

  const {
    data: notesData,
    isLoading: notesLoading,
    isFetching: notesFetching,
    isError: hasNotesError,
    error: notesError,
  } = useNotesList(notesQueryParams);
  const { toggleBookmark, toggleLike } = useNotesListActions(notesQueryParams);

  const isNotesListPending = notesLoading || notesFetching;
  const visiblePages = buildVisiblePages(Number(notesData?.pagination?.currentPage || currentPage), Number(notesData?.pagination?.totalPages || 1));

  const listScreenState = useMemo(
    () =>
      buildQuestionBankFlowState({
        ...flowState,
        listQueryState: {
          currentPage,
          searchTerm,
          appliedFilters,
        },
      }),
    [appliedFilters, currentPage, flowState, searchTerm]
  );

  useEffect(() => {
    persistQuestionBankFlowState(listScreenState);
  }, [listScreenState]);

  useEffect(() => {
    const debounceTimer = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => window.clearTimeout(debounceTimer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedFilters, debouncedSearchTerm]);

  useEffect(() => {
    if (!isFilterOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsFilterOpen(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFilterOpen]);

  const resolvedStateName =
    notesData?.stateName ||
    resolveQuestionBankStateName({
      locationState: flowState,
      requestParams: filterQueryParams,
      sidebarData,
    }) ||
    "State not selected";

  const resolvedTitle =
    notesData?.topicName ||
    notesData?.subjectName ||
    flowState?.selectedSubtopic?.name ||
    flowState?.selectedTopic?.name ||
    flowState?.selectedSubject?.title ||
    "Notes";

  const headerTitleParts = String(resolvedTitle).trim().split(/\s+/).filter(Boolean);
  const headerFirst = headerTitleParts.slice(0, -1).join(" ") || resolvedTitle;
  const headerLast = headerTitleParts.length > 1 ? headerTitleParts.slice(-1).join(" ") : "";
  const loadError = hasNotesError ? notesError?.response?.data?.message || notesError?.message || "Unable to load notes." : "";

  const handleBackNavigation = () => {
    if (flowState?.qbankBackTarget?.pathname) {
      navigate(flowState.qbankBackTarget.pathname, {
        replace: true,
        state: flowState.qbankBackTarget.state,
      });
      return;
    }

    navigate(EXPLORE_ROUTES.subjects, {
      replace: true,
      state: buildQuestionBankSubjectsState(flowState),
    });
  };

  const handleBookmarkToggle = (note) => {
    if (!note?.id || toggleBookmark.isPending) {
      return;
    }

    toggleBookmark.mutate({
      notes_id: Number(note.id),
    });
  };

  const handleLikeToggle = (note) => {
    if (!note?.id || toggleLike.isPending) {
      return;
    }

    toggleLike.mutate({
      notes_id: Number(note.id),
    });
  };

  const openReportModal = (note) => {
    setSelectedNote(note || null);
    setIsReportModalOpen(true);
  };

  const openNotesModal = (note) => {
    setSelectedNote(note || null);
    setIsNotesModalOpen(true);
  };

  const openViewModal = (note) => {
    setSelectedNote(note || null);
    setIsViewModalOpen(true);
  };

  const openNoteDetail = async (note) => {
    if (!note?.id) {
      return;
    }

    const noteContext = noteDetailFlow(note, { ...listScreenState, ...notesQueryParams }, notesData?.raw);
    if (noteNeedsPurchase(note)) {
      await promptNoteSubscription(note, noteContext, navigate, notesData?.raw);
      return;
    }

    const detailFlowState = buildQuestionBankFlowState({
        ...noteContext,
        selectedNote: note,
        note_id: Number(note.id),
        qbankBackTarget: {
          pathname: CONTENT_ROUTES.notes,
          state: listScreenState,
        },
      });

    persistQuestionBankFlowState(detailFlowState);
    navigate(CONTENT_ROUTES.noteDetail, {
      state: detailFlowState,
    });
  };

  return (
    <>
      <style>{`
 .homeDashboard-wrapper .page-content {
  padding-right: 0;
}
      `}</style>
      <HomeDashboardLayout showHeader={false}>
        <div className="qbl-page qbankList-page">
          <div className="qbl-main" aria-label="Notes content">
            <section className="qbl-panel">
              <div className="d-flex justify-content-between align-items-center d-xl-none">
                <button type="button" className="languageImg d-flex d-xl-none doubt-back border-0 bg-transparent p-0" onClick={handleBackNavigation} aria-label="Go back">
                  <BackSVG />
                </button>
                <h4 className="fw-bold mb-0 blackText">Notes</h4>
                <div className="d-flex gap-2">
                  <div className="languageImg d-flex d-xl-none">
                    <LanguageMobileIcon />
                  </div>
                </div>
              </div>

              <div className="qb-body">
                <div className="d-flex align-items-center justify-content-center">
                  <div className="qb-copy">
                    <div className="qb-loc">
                      <i className="bi bi-geo-alt-fill" />
                      <span> {resolvedStateName}</span>
                    </div>

                    <h1 className="qb-title">
                      {headerFirst}
                      {headerLast ? <span>{` ${headerLast}`}</span> : null}
                    </h1>

                    <p className="qb-sub qbb-list">Read more, revise better!</p>
                  </div>
                </div>
              </div>

              <div className="d-flex d-xl-block align-items-center mobile-filter-box">
                <label className="qbl-search">
                  <i className="bi bi-search" aria-hidden="true" />
                  <input type="text" placeholder="Search notes..." value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} disabled={isNotesListPending} />
                </label>

                <button type="button" className="mobile-Filter-icon d-flex d-lg-flex d-xl-none" onClick={() => setIsFilterOpen(true)} disabled={isNotesListPending} aria-label="Open filters" aria-expanded={isFilterOpen} aria-controls="qbank-filter-drawer">
                  <span className="filter-card__icon">
                    <i className="bi bi-sliders2"></i>
                  </span>
                </button>
              </div>

              {isNotesListPending ? (
                <SummaryRowShimmer />
              ) : (
                <div className="qbl-summaryRow">
                  <h2>{`${Number(notesData?.totalResults || 0)} Notes Found`}</h2>
                </div>
              )}

              <div className="qbl-questionList" aria-label="Notes list">
                {isNotesListPending ? Array.from({ length: 6 }).map((_, index) => <NoteCardShimmer key={index} />) : null}
                {!isNotesListPending && loadError ? <div className="choose-state__status choose-state__status--error">{loadError}</div> : null}
                {!isNotesListPending && !loadError && !(notesData?.notes || []).length ? <div className="choose-state__status">No notes available.</div> : null}
                {!isNotesListPending && !loadError
                  ? (notesData?.notes || []).map((item) => (
                      <NoteCard
                        key={item.id}
                        item={item}
                        onBookmark={handleBookmarkToggle}
                        onLike={handleLikeToggle}
                        onView={openViewModal}
                        onReport={openReportModal}
                        onNotes={openNotesModal}
                        onOpen={openNoteDetail}
                        isBookmarkUpdating={toggleBookmark.isPending && Number(toggleBookmark.variables?.notes_id) === Number(item.id)}
                        isLikeUpdating={toggleLike.isPending && Number(toggleLike.variables?.notes_id) === Number(item.id)}
                      />
                    ))
                  : null}
              </div>

              {!isNotesListPending && !loadError && Number(notesData?.pagination?.totalPages || 1) > 1 ? (
                <div className="qbl-pagination">
                  <button type="button" className="qbl-pagination__nav" disabled={currentPage <= 1} onClick={() => setCurrentPage((previousPage) => Math.max(1, previousPage - 1))}>
                    Previous
                  </button>
                  <div className="qbl-pagination__pages">
                    {visiblePages.map((pageNumber) => (
                      <button key={pageNumber} type="button" className={`qbl-pagination__page${pageNumber === currentPage ? " is-active" : ""}`} onClick={() => setCurrentPage(pageNumber)}>
                        {pageNumber}
                      </button>
                    ))}
                  </div>
                  <button type="button" className="qbl-pagination__nav" disabled={currentPage >= Number(notesData?.pagination?.totalPages || 1)} onClick={() => setCurrentPage((previousPage) => Math.min(Number(notesData?.pagination?.totalPages || 1), previousPage + 1))}>
                    Next
                  </button>
                </div>
              ) : null}
            </section>
          </div>

          <div className={`qbl-filterBackdrop ${isFilterOpen ? "is-open" : ""}`} onClick={() => setIsFilterOpen(false)} aria-hidden="true" />

          <div id="qbank-filter-drawer" className={`qbl-filterPanel ${isFilterOpen ? "is-open" : ""}`}>
            <QBankFilter
              className={isFilterOpen ? "is-open" : ""}
              onClose={() => setIsFilterOpen(false)}
              onApply={(nextFilters) => {
                setAppliedFilters(nextFilters || {});
                setIsFilterOpen(false);
              }}
              appliedFilters={appliedFilters}
              queryParams={filterQueryParams}
            />
          </div>

          <NotesViewModal open={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} note={selectedNote} />
          <NotesReportModal open={isReportModalOpen} onClose={() => setIsReportModalOpen(false)} note={selectedNote} notesQueryParams={notesQueryParams} />
          <NotesStickyNotesPopup open={isNotesModalOpen} onClose={() => setIsNotesModalOpen(false)} note={selectedNote} notesQueryParams={notesQueryParams} />
        </div>
      </HomeDashboardLayout>
    </>
  );
};

export default NotesList;
